import moment from 'moment';
import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { DateTimeInput } from 'semantic-ui-calendar-react-17';
import { Button, DropdownItemProps, Modal } from 'semantic-ui-react';
import { GtfsFileStateType } from '../../types/gtfsFilesTypes';
import SemanticDataTable, { SemanticDataTableColumn } from '../shared/semanticTable/SemanticDataTable';
import GtfsControls from './GtfsControls';
import { ModalSchedulerGtfs } from './ModalSchedulerGtfs';

type RoleType = string | string[] | undefined;
type GtfsListItem = GtfsFileStateType & { autoPublish?: boolean };
type ActivationEditorState = { gtfsId: string; value: string | Date | null } | undefined;

interface Props {
    gtfsFiles: GtfsListItem[];
    role: RoleType;
    updateActivationDate: (gtfsId: string, activationDate: string | Date | null) => void;
    onDetailsClick: (value: string) => void;
    onGtfsViewClick: (value: string) => void;
}

const isAdmin = (roles: RoleType) => {
    if (roles === undefined) {
        return false;
    }

    if (typeof roles === 'string') {
        return roles.toLowerCase() === 'admin';
    }

    if (Array.isArray(roles)) {
        return roles
            .map((value) => value.toLowerCase())
            .includes('admin');
    }

    return false;
};

const validGtfsStatus = (gtfs: GtfsListItem) => gtfs.status === 'Scheduled' || gtfs.status === 'New' || gtfs.status === 'Unpublished';

const isDisabled = (roles: RoleType, gtfs: GtfsListItem) => !isAdmin(roles) || !gtfs.valid || !validGtfsStatus(gtfs);

const isClearable = (roles: RoleType, gtfs: GtfsListItem) => !isDisabled(roles, gtfs) && !!gtfs.activationDate && gtfs.status !== 'Scheduled';

const formatDateTime = (value?: Date | string | null) => value ? moment(value).format('lll') : '';
const formatDate = (value?: Date | string | null) => value ? moment(value).format('ll') : '';
const formatActivationDate = (value?: Date | string | null) => value ? moment.parseZone(value).format('YYYY-MM-DD h:mm A') : 'Immediately';
const toTimestamp = (value?: Date | string | null) => value ? moment(value).valueOf() : Number.MIN_SAFE_INTEGER;

const buildStatusOptions = (gtfsFiles: GtfsListItem[]): DropdownItemProps[] => {
    return Array.from(new Set(gtfsFiles.map((file) => file.status).filter((value): value is string => value.length > 0)))
        .sort((firstValue, secondValue) => firstValue.localeCompare(secondValue))
        .map((value) => ({
            key: value,
            text: value,
            value,
        }));
};

const validityOptions: DropdownItemProps[] = [
    { key: 'valid', text: 'Valid', value: 'Valid' },
    { key: 'invalid', text: 'Invalid', value: 'Invalid' },
];

const fixedColumnStyles: Record<string, React.CSSProperties> = {
    gtfsFileId: { width: 260, minWidth: 260, maxWidth: 260 },
    originalName: { width: 240, minWidth: 240, maxWidth: 240 },
    status: { width: 130, minWidth: 130, maxWidth: 130 },
    valid: { width: 120, minWidth: 120, maxWidth: 120 },
    uploadedUtc: { width: 170, minWidth: 170, maxWidth: 170 },
    calendarFrom: { width: 150, minWidth: 150, maxWidth: 150 },
    calendarTo: { width: 150, minWidth: 150, maxWidth: 150 },
    activationDate: { width: 220, minWidth: 220, maxWidth: 220 },
    actions: { width: 170, minWidth: 170, maxWidth: 170 },
};

const GtfsTable: React.FC<Props> = ({
    gtfsFiles,
    role,
    updateActivationDate,
    onDetailsClick,
    onGtfsViewClick,
}) => {
    const [scheduledGtfsState, setScheduledGtfsState] = useState<GtfsListItem | undefined>(undefined);
    const [activationEditorState, setActivationEditorState] = useState<ActivationEditorState>(undefined);

    const onUpdateDate = useCallback((gtfsId: string, value: string | Date | null) => {
        setScheduledGtfsState(undefined);
        setActivationEditorState(undefined);
        updateActivationDate(gtfsId, value);
    }, [updateActivationDate]);

    const openActivationEditor = useCallback((gtfs: GtfsListItem) => {
        if (isDisabled(role, gtfs)) {
            return;
        }

        setActivationEditorState({
            gtfsId: gtfs.gtfsFileId,
            value: gtfs.activationDate,
        });
    }, [role]);

    const statusOptions = useMemo(() => buildStatusOptions(gtfsFiles), [gtfsFiles]);

    const selectedActivationGtfs = activationEditorState
        ? gtfsFiles.find((gtfs) => gtfs.gtfsFileId === activationEditorState.gtfsId)
        : undefined;

    const columns = useMemo<SemanticDataTableColumn<GtfsListItem>[]>(() => {
        const admin = isAdmin(role);

        const baseColumns: SemanticDataTableColumn<GtfsListItem>[] = [
            {
                id: 'gtfsFileId',
                title: 'Cloud Name',
                render: (gtfs) => (
                    <button
                        type="button"
                        className="semanticTableClickable"
                        onClick={(event) => {
                            event.stopPropagation();
                            onGtfsViewClick(gtfs.gtfsFileId);
                        }}>
                        {gtfs.gtfsFileId}
                    </button>
                ),
                getSortValue: (gtfs) => gtfs.gtfsFileId,
                getFilterValue: (gtfs) => gtfs.gtfsFileId,
                headerStyle: fixedColumnStyles.gtfsFileId,
                cellStyle: fixedColumnStyles.gtfsFileId,
                filter: {
                    type: 'text',
                    debounceMs: 300,
                    placeholder: 'Cloud name...',
                },
            },
            {
                id: 'originalName',
                title: 'Original Name',
                render: (gtfs) => gtfs.originalName,
                getSortValue: (gtfs) => gtfs.originalName,
                getFilterValue: (gtfs) => gtfs.originalName,
                headerStyle: fixedColumnStyles.originalName,
                cellStyle: fixedColumnStyles.originalName,
                filter: {
                    type: 'text',
                    debounceMs: 300,
                    placeholder: 'Original name...',
                },
            },
            {
                id: 'status',
                title: 'Status',
                render: (gtfs) => gtfs.status,
                getSortValue: (gtfs) => gtfs.status,
                getFilterValue: (gtfs) => gtfs.status,
                headerStyle: fixedColumnStyles.status,
                cellStyle: fixedColumnStyles.status,
                filter: {
                    type: 'select',
                    placeholder: 'Any status',
                    options: statusOptions,
                },
            },
            {
                id: 'valid',
                title: 'Validity',
                render: (gtfs) => gtfs.valid === true ? 'Valid' : gtfs.valid === false ? 'Invalid' : '',
                getSortValue: (gtfs) => gtfs.valid === true ? 1 : gtfs.valid === false ? 0 : -1,
                getFilterValue: (gtfs) => gtfs.valid === true ? 'Valid' : gtfs.valid === false ? 'Invalid' : '',
                headerStyle: fixedColumnStyles.valid,
                cellStyle: fixedColumnStyles.valid,
                filter: {
                    type: 'select',
                    placeholder: 'Any validity',
                    options: validityOptions,
                },
            },
            {
                id: 'uploadedUtc',
                title: 'Uploaded Utc',
                render: (gtfs) => <span className="td-nowrap">{formatDateTime(gtfs.uploadedUtc)}</span>,
                getSortValue: (gtfs) => toTimestamp(gtfs.uploadedUtc),
                getFilterValue: (gtfs) => formatDateTime(gtfs.uploadedUtc),
                headerStyle: fixedColumnStyles.uploadedUtc,
                cellStyle: fixedColumnStyles.uploadedUtc,
                filter: {
                    type: 'text',
                    debounceMs: 300,
                    placeholder: 'Uploaded date...',
                },
            },
            {
                id: 'calendarFrom',
                title: 'Calendar From',
                render: (gtfs) => <span className="td-nowrap">{formatDate(gtfs.calendarFrom)}</span>,
                getSortValue: (gtfs) => toTimestamp(gtfs.calendarFrom),
                getFilterValue: (gtfs) => formatDate(gtfs.calendarFrom),
                headerStyle: fixedColumnStyles.calendarFrom,
                cellStyle: fixedColumnStyles.calendarFrom,
                filter: {
                    type: 'text',
                    debounceMs: 300,
                    placeholder: 'Start date...',
                },
            },
            {
                id: 'calendarTo',
                title: 'Calendar To',
                render: (gtfs) => <span className="td-nowrap">{formatDate(gtfs.calendarTo)}</span>,
                getSortValue: (gtfs) => toTimestamp(gtfs.calendarTo),
                getFilterValue: (gtfs) => formatDate(gtfs.calendarTo),
                headerStyle: fixedColumnStyles.calendarTo,
                cellStyle: fixedColumnStyles.calendarTo,
                filter: {
                    type: 'text',
                    debounceMs: 300,
                    placeholder: 'End date...',
                },
            },
            {
                id: 'activationDate',
                title: 'Activation Date',
                render: (gtfs) => (
                    isDisabled(role, gtfs)
                        ? <span className="td-nowrap">{formatActivationDate(gtfs.activationDate)}</span>
                        : <button
                            type="button"
                            className="semanticTableClickable td-nowrap"
                            onClick={(event) => {
                                event.stopPropagation();
                                openActivationEditor(gtfs);
                            }}>
                            {formatActivationDate(gtfs.activationDate)}
                        </button>
                ),
                getSortValue: (gtfs) => toTimestamp(gtfs.activationDate),
                getFilterValue: (gtfs) => formatActivationDate(gtfs.activationDate),
                headerStyle: fixedColumnStyles.activationDate,
                cellStyle: fixedColumnStyles.activationDate,
            },
        ];

        if (admin) {
            baseColumns.push({
                id: 'actions',
                title: '',
                sortable: false,
                headerStyle: fixedColumnStyles.actions,
                cellStyle: fixedColumnStyles.actions,
                render: (gtfs) => (
                    <div onClick={(event) => event.stopPropagation()}>
                        <GtfsControls
                            gtfsFile={gtfs}
                            onDetailsClick={() => onDetailsClick(gtfs.gtfsFileId)}
                            onScheduleClick={() => setScheduledGtfsState({ ...gtfs })}
                        />
                    </div>
                ),
            });
        }

        return baseColumns;
    }, [onDetailsClick, onGtfsViewClick, openActivationEditor, role, statusOptions]);

    return (
        <>
            <SemanticDataTable<GtfsListItem>
                columns={columns}
                data={gtfsFiles}
                rowKey={(gtfs) => gtfs.gtfsFileId}
                className="semanticTable"
                emptyMessage="No GTFS files match the current filters."
                defaultSortColumnId="uploadedUtc"
                defaultSortDirection="descending"
                height={720}
                singleLine={false}
                fixed={true}
            />
            {selectedActivationGtfs && activationEditorState && (
                <Modal open={true} dimmer="inverted" onClose={() => setActivationEditorState(undefined)}>
                    <Modal.Header>Update activation date</Modal.Header>
                    <Modal.Content>
                        <DateTimeInput
                            name="activationDateCalendar"
                            fluid
                            clearable={isClearable(role, selectedActivationGtfs)}
                            dateFormat="YYYY-MM-DD"
                            timeFormat="AMPM"
                            value={activationEditorState.value ? moment.parseZone(activationEditorState.value).format('YYYY-MM-DD h:mm A') : ''}
                            iconPosition="left"
                            popupPosition="bottom center"
                            closable={true}
                            animation="fade"
                            onClear={() => setActivationEditorState((previousState) => previousState ? { ...previousState, value: null } : previousState)}
                            onChange={(_event, { value }) => setActivationEditorState((previousState) => previousState ? { ...previousState, value } : previousState)}
                        />
                    </Modal.Content>
                    <Modal.Actions>
                        <Button onClick={() => setActivationEditorState(undefined)}>Cancel</Button>
                        {isClearable(role, selectedActivationGtfs) && (
                            <Button onClick={() => onUpdateDate(selectedActivationGtfs.gtfsFileId, null)}>Clear</Button>
                        )}
                        <Button primary onClick={() => onUpdateDate(selectedActivationGtfs.gtfsFileId, activationEditorState.value)}>Apply</Button>
                    </Modal.Actions>
                </Modal>
            )}
            {scheduledGtfsState && (
                <ModalSchedulerGtfs
                    gtfs={scheduledGtfsState}
                    open={true}
                    okButtonHandler={(value) => onUpdateDate(scheduledGtfsState.gtfsFileId, value)}
                    cancelButtonHandler={() => setScheduledGtfsState(undefined)}
                />
            )}
        </>
    );
};

export default GtfsTable;
